import { useEffect, useRef, useState } from "react";
import { Lock } from "lucide-react";
import { useBookingStatus } from "../contexts/BookingStatusContext";

const VISIBLE_MS = 10000; // auto-dismiss after ~10s
const FADE_MS = 400;

/**
 * Fixed banner pinned above the navigation. When booking is closed it shows the
 * admin's message to every visitor (logged in or not), auto-dismisses after ~10s,
 * and publishes its own height as the `--banner-h` CSS variable so the fixed <nav>
 * can offset below it.
 */
export function BookingClosedBanner() {
  const { bookingEnabled, bookingClosedMessage } = useBookingStatus();
  const closed = !bookingEnabled;
  const ref = useRef<HTMLDivElement>(null);
  const [dismissed, setDismissed] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);

  // Auto-dismiss timer. Restarts whenever the closed state / message changes
  // (e.g. an admin closes booking again), so the banner re-shows for ~10s.
  useEffect(() => {
    if (!closed) return;
    setDismissed(false);
    setFadeOut(false);
    const fadeTimer = setTimeout(() => setFadeOut(true), VISIBLE_MS);
    const removeTimer = setTimeout(() => setDismissed(true), VISIBLE_MS + FADE_MS);
    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(removeTimer);
    };
  }, [closed, bookingClosedMessage]);

  const show = closed && !dismissed;

  // Publish banner height so the fixed nav offsets below it; reset when hidden.
  useEffect(() => {
    const root = document.documentElement;
    if (!show) {
      root.style.setProperty("--banner-h", "0px");
      return;
    }
    const el = ref.current;
    if (!el) return;

    const setVar = () => root.style.setProperty("--banner-h", `${el.offsetHeight}px`);
    setVar();
    const observer = new ResizeObserver(setVar);
    observer.observe(el);

    return () => {
      observer.disconnect();
      root.style.setProperty("--banner-h", "0px");
    };
  }, [show, bookingClosedMessage]);

  if (!show) return null;

  // Inline styles: the project's frozen Tailwind CSS lacks these utilities
  // (bg-red-600, inset-x-0, z-[60]), so classes alone would render invisibly.
  return (
    <div
      ref={ref}
      role="alert"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 60,
        backgroundColor: "#dc2626",
        color: "#ffffff",
        boxShadow: "0 1px 4px rgba(0,0,0,.2)",
        opacity: fadeOut ? 0 : 1,
        transition: `opacity ${FADE_MS}ms ease`,
      }}
    >
      <div
        style={{
          maxWidth: "80rem",
          margin: "0 auto",
          padding: "0.625rem 1rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "0.5rem",
          textAlign: "center",
        }}
      >
        <Lock style={{ width: "1rem", height: "1rem", flexShrink: 0 }} aria-hidden="true" />
        <p style={{ fontSize: "0.875rem", fontWeight: 500, whiteSpace: "pre-line" }}>
          {bookingClosedMessage}
        </p>
      </div>
    </div>
  );
}
